import { defineStore } from 'pinia';
import tenantService from '@/services/tenantService';
import api from '@/services/api';

export const useTenantStore = defineStore('tenant', {
  state: () => ({
    // Informations du tenant
    config: {
      id: null,
      nomEtablissement: 'OpSolver',
      logoUrl: null,
      couleurPrimaire: '#2563eb',
      couleurSecondaire: '#3b82f6',
      faviconUrl: null,
      bgImage: null,
      adresse: null,
      telephone: null,
      email: null,
      siteWeb: null,
      description: null,
      timezone: 'Africa/Kinshasa',
      langue: 'fr'
    },
    
    // États de chargement
    loading: false,
    saving: false,
    uploading: false,
    
    // État d'initialisation
    isInitialized: false,
    
    // État de configuration manquante
    isConfigMissing: false,
    
    // Erreurs
    error: null
  }),

  getters: {
    /**
     * Retourne le nom de l'établissement
     */
    tenantName: (state) => state.config.nomEtablissement || 'OpSolver',
    
    /**
     * Retourne l'URL complète du logo ou un logo par défaut
     */
    logoUrl: (state) => {
      if (state.config.logoUrl) {
        // Si l'URL commence par http/https, la retourner telle quelle
        if (state.config.logoUrl.startsWith('http')) {
          return state.config.logoUrl;
        }
        // Sinon, construire l'URL complète avec la base API
        const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        return `${API_URL}${state.config.logoUrl}`;
      }
      return '/assets/images/opsolver-logo.png';
    },
    
    /**
     * Retourne l'URL complète du favicon ou un favicon par défaut
     */
    faviconUrl: (state) => {
      if (state.config.faviconUrl) {
        // Si l'URL commence par http/https, la retourner telle quelle
        if (state.config.faviconUrl.startsWith('http')) {
          return state.config.faviconUrl;
        }
        // Sinon, construire l'URL complète avec la base API
        const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        return `${API_URL}${state.config.faviconUrl}`;
      }
      return '/favicon.ico';
    },
    
    /**
     * Retourne l'URL complète de l'image de fond ou null
     */
    bgImageUrl: (state) => {
      if (state.config.bgImage) {
        // Si l'URL commence par http/https, la retourner telle quelle
        if (state.config.bgImage.startsWith('http')) {
          return state.config.bgImage;
        }
        // Sinon, construire l'URL complète avec la base API
        const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        return `${API_URL}${state.config.bgImage}`;
      }
      return null;
    },
    
    /**
     * Retourne la couleur primaire
     */
    primaryColor: (state) => state.config.couleurPrimaire || '#2563eb',
    
    /**
     * Retourne la couleur secondaire
     */
    secondaryColor: (state) => state.config.couleurSecondaire || '#3b82f6',
    
    /**
     * Retourne si les données sont prêtes à être utilisées
     */
    isReady: (state) => state.isInitialized && !state.loading,
    
    /**
     * Retourne si l'application nécessite une configuration initiale
     */
    needsInitialSetup: (state) => state.isConfigMissing && state.isInitialized,
    
    /**
     * Retourne les informations complètes pour l'affichage
     */
    displayInfo: (state, getters) => ({
      name: state.config.nomEtablissement || 'OpSolver',
      logo: getters.logoUrl,
      address: state.config.adresse,
      phone: state.config.telephone,
      email: state.config.email,
      website: state.config.siteWeb,
      description: state.config.description,
      favicon: getters.faviconUrl
    })
  },

  actions: {
    /**
     * Charge la configuration du tenant depuis l'API
     */
    async loadConfig() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await tenantService.getConfig();
        
        // Vérifier si c'est une vraie configuration ou des données par défaut
        if (response && response.success && response.data) {
          const data = response.data;
          
          // Si le nom est "Mon Établissement" ou s'il n'y a pas d'ID, c'est probablement une config par défaut
          const isDefaultConfig = !data.id || 
                                  data.nomEtablissement === 'Mon Établissement' ||
                                  (!data.nomEtablissement || data.nomEtablissement === 'OpSolver');
          
          if (isDefaultConfig) {
            console.log('Configuration par défaut détectée, setup initial requis');
            this.isConfigMissing = true;
          } else {
            this.config = { ...this.config, ...data };
            this.isConfigMissing = false;
            
            // Appliquer le branding dynamiquement
            this.applyBranding();
          }
        } else {
          this.isConfigMissing = true;
        }
        
        this.isInitialized = true;
        return this.config;
      } catch (error) {
        console.warn('Configuration tenant non trouvée, configuration initiale requise');
        this.error = error.message;
        this.isConfigMissing = true;
        this.isInitialized = true;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Charge uniquement le branding (logo, couleurs) - accessible sans authentification
     */
    async loadBranding() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await tenantService.getBranding();
        
        if (response && response.success && response.data) {
          // Appliquer seulement les éléments de branding
          if (response.data.nomEtablissement) {
            this.config.nomEtablissement = response.data.nomEtablissement;
          }
          if (response.data.logoUrl) {
            this.config.logoUrl = response.data.logoUrl;
          }
          if (response.data.couleurPrimaire) {
            this.config.couleurPrimaire = response.data.couleurPrimaire;
          }
          if (response.data.couleurSecondaire) {
            this.config.couleurSecondaire = response.data.couleurSecondaire;
          }
          if (response.data.faviconUrl) {
            this.config.faviconUrl = response.data.faviconUrl;
          }
          if (response.data.bgImage) {
            this.config.bgImage = response.data.bgImage;
          }
          
          // Appliquer le branding
          this.applyBranding();
        }
        
        return this.config;
      } catch (error) {
        console.log('Branding non disponible, utilisation des valeurs par défaut');
        // Ne pas marquer comme erreur, c'est normal si pas encore configuré
      } finally {
        this.loading = false;
      }
    },

    /**
     * Crée la configuration initiale du tenant
     */
    async createInitialConfig(configData) {
      this.saving = true;
      this.error = null;
      
      try {
        const response = await tenantService.updateConfig(configData);
        
        if (response && response.success) {
          this.config = { ...this.config, ...response.data };
          this.isConfigMissing = false;
          
          // Réappliquer le branding avec les nouvelles valeurs
          this.applyBranding();
          
          return response;
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    },

    /**
     * Met à jour la configuration du tenant
     */
    async updateConfig(newConfig) {
      this.saving = true;
      this.error = null;
      
      try {
        const response = await tenantService.updateConfig(newConfig);
        
        if (response && response.success) {
          this.config = { ...this.config, ...response.data };
          
          // Réappliquer le branding avec les nouvelles valeurs
          this.applyBranding();
          
          return response;
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.saving = false;
      }
    },

    /**
     * Upload du logo
     */
    async uploadLogo(logoFile) {
      this.uploading = true;
      this.error = null;
      
      try {
        const response = await tenantService.uploadLogo(logoFile);
        
        if (response && response.success) {
          this.config.logoUrl = response.logoUrl;
          this.applyBranding();
          return response;
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.uploading = false;
      }
    },

    /**
     * Upload du favicon
     */
    async uploadFavicon(faviconFile) {
      this.uploading = true;
      this.error = null;
      
      try {
        const response = await tenantService.uploadFavicon(faviconFile);
        
        if (response && response.success) {
          this.config.faviconUrl = response.faviconUrl;
          this.applyFavicon();
          return response;
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.uploading = false;
      }
    },

    /**
     * Upload de l'image de fond
     */
    async uploadBackground(backgroundFile) {
      this.uploading = true;
      this.error = null;
      
      try {
        const response = await tenantService.uploadBackground(backgroundFile);
        
        if (response && response.success) {
          this.config.bgImage = response.bgImage;
          return response;
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.uploading = false;
      }
    },

    /**
     * Applique le branding du tenant à l'interface
     */
    applyBranding() {
      // Change le titre de la page
      document.title = `OpSolver - ${this.tenantName}`;
      
      // Applique les couleurs personnalisées via les variables CSS
      const root = document.documentElement;
      root.style.setProperty('--tenant-primary-color', this.primaryColor);
      root.style.setProperty('--tenant-secondary-color', this.secondaryColor);
      
      // Applique le favicon
      this.applyFavicon();
    },

    /**
     * Applique le favicon personnalisé
     */
    applyFavicon() {
      if (this.config.faviconUrl) {
        let favicon = document.querySelector('link[rel="icon"]');
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = this.config.faviconUrl;
      }
    },

    /**
     * Réinitialise les erreurs
     */
    clearError() {
      this.error = null;
    }
  }
});
